import React, { ElementType, PropsWithChildren, ReactNode } from 'react';

import classNames from 'classnames';

type HeadingTags = 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
type CaptionTags =
  | 'caption-m'
  | 'caption-m-strong'
  | 'caption-m-mono'
  | 'caption-s'
  | 'caption-s-strong'
  | 'caption-s-mono'
  | 'caption-xs'
  | 'caption-xs-strong'
  | 'caption-xss'
  | 'caption-xss-strong';

export type TypographyVariants = HeadingTags | CaptionTags | 'display';

export type TypographyColors =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'pink'
  | 'purple'
  | 'blue'
  | 'orange'
  | 'green'
  | 'red'
  | 'white';

const TYPOGRAPHY_STYLES: Record<TypographyVariants, { base?: string; desktop?: string }> = {
  display: {
    base: 'text-[32px] leading-[36px] font-semibold',
    desktop: 'xl-msq:text-[40px] xl-msq:leading-[44px]',
  },
  h1: {
    base: 'text-[28px] leading-[32px] font-semibold',
    desktop: 'xl-msq:text-[32px] xl-msq:leading-[36px]',
  },
  h2: {
    base: 'text-[24px] leading-[28px] font-semibold',
    desktop: 'xl-msq:text-[28px] xl-msq:leading-[32px]',
  },
  h3: {
    base: 'text-[20px] leading-[24px] font-semibold',
    desktop: 'xl-msq:text-[24px] xl-msq:leading-[28px]',
  },
  h4: {
    base: 'text-[18px] leading-[22px] font-semibold',
    desktop: 'xl-msq:text-[20px] xl-msq:leading-[24px]',
  },
  h5: {
    base: 'text-[16px] leading-[20px] font-semibold',
  },
  'caption-m': {
    base: 'text-[16px] leading-[20px] font-medium',
  },
  'caption-m-strong': {
    base: 'text-[16px] leading-[20px] font-semibold',
  },
  'caption-m-mono': {
    base: 'text-[16px] leading-[20px] font-lining-nums font-tabular-nums font-medium',
  },
  'caption-s': {
    base: 'text-[14px] leading-[20px] font-medium',
  },
  'caption-s-strong': {
    base: 'text-[14px] leading-[20px] font-semibold',
  },
  'caption-s-mono': {
    base: 'text-[14px] leading-[20px] font-lining-nums font-tabular-nums font-medium',
  },
  'caption-xs': {
    base: 'text-[13px] leading-[16px] font-medium',
  },
  'caption-xs-strong': {
    base: 'text-[13px] leading-[16px] font-semibold',
  },
  'caption-xss': {
    base: 'text-[11px] leading-[14px] font-medium',
  },
  'caption-xss-strong': {
    base: 'text-[11px] leading-[14px] font-semibold font-medium',
  },
};

type TypographyBaseProps = {
  /**
   * Color applied to content
   * @default primary
   */
  color?: TypographyColors;
  /**
   * The component used for the root node
   */
  component?: ElementType;
  /**
   * Text ellipsis
   * @default false
   */
  isEllipsis?: boolean;
  /**
   * If `true`, the component will have a full width
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Prevents text wrapping, ensuring the content stays on a single line
   * @default false
   */
  noWrap?: boolean;
  /**
   * Text alignment
   * @default left
   */
  textAlign?: 'left' | 'right' | 'center' | 'justify';
  /**
   * Define text transformation applied to content
   */
  transform?: 'capitalize' | 'lowercase' | 'uppercase';
  /**
   * Defines the typography style variant to apply
   * @default caption-m
   */
  variant?: TypographyVariants;
  /**
   * Title attribute of the element. Used by screen readers
   * Use it with `isEllipsis`
   */
  title?: string;
};

interface TypographyPropsWithEllipsis extends TypographyBaseProps {
  isEllipsis: true;
  title: string;
}

interface TypographyPropsWithoutEllipsis extends TypographyBaseProps {
  isEllipsis?: false;
  title?: string;
}

export type TypographyProps = TypographyPropsWithEllipsis | TypographyPropsWithoutEllipsis;

export const Typography = ({
  children,
  color = 'primary',
  component,
  isEllipsis = false,
  isFullWidth = false,
  noWrap = false,
  textAlign = 'left',
  title,
  transform,
  variant = 'caption-m',
}: PropsWithChildren<TypographyProps>) => {
  const { base, desktop } = TYPOGRAPHY_STYLES[variant];
  const isCaption = variant.startsWith('caption');
  const isCaptionOrDisplay = isCaption || variant === 'display';

  const defaultComponent = isCaptionOrDisplay ? 'span' : (variant as HeadingTags);
  const Component = (component ?? defaultComponent) as ElementType<{ children: ReactNode }>;

  return (
    <Component
      className={classNames(base, desktop, `text-base-text-${color}`, {
        'truncate block': isEllipsis,
        [transform || '']: !!transform,
        'whitespace-nowrap': noWrap,
        'font-primary': isCaption,
        'font-secondary': !isCaption,
        'w-full': isFullWidth,
        'inline-block': isCaptionOrDisplay,
        'text-left': textAlign === 'left',
        'text-right': textAlign === 'right',
        'text-center': textAlign === 'center',
        'text-justify': textAlign === 'justify',
      })}
      title={title}
    >
      {children}
    </Component>
  );
};
